import React, { useState, useEffect, useMemo } from 'react';
import { EntityType } from '../types';
import { limsClient } from '../api/limsClient';
import { formatDate } from '../utils/formatDate';
import { formatValue as formatValueStr } from '../utils/formatValue';
import type { EntityHistory as EntityHistoryType, EventLogEntry } from '@mariana/labos-client';
import { WorksheetDiffViewer } from './WorksheetDiffViewer';
import { KeyValueDiffViewer } from './KeyValueDiffViewer';
import './EntityHistory.css';

type TimelineEntry =
  | { type: 'snapshot'; timestamp: Date; version: number; isCreation: boolean; changes: Array<{ attr: string; oldValue: any; newValue: any }> }
  | { type: 'event'; timestamp: Date; event: EventLogEntry };

const EditIcon = () => (
  <svg className="tl-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const EventIcon = () => (
  <svg className="tl-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const CreateIcon = () => (
  <svg className="tl-type-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

const ExpandableJson: React.FC<{ value: string }> = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = value.length > 80;

  if (!isLong) {
    return <code className="tl-listener-result json-inline">{value}</code>;
  }

  if (!expanded) {
    return (
      <span className="json-collapsed-row">
        <code className="tl-listener-result json-inline json-truncated">{value.slice(0, 60)}…</code>
        <button className="json-toggle-btn" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>Show</button>
      </span>
    );
  }

  return (
    <div className="json-expandable">
      <pre className="tl-listener-result json-expanded">{value}</pre>
      <button className="json-toggle-btn" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>Hide</button>
    </div>
  );
};

function formatTimeDelta(ms: number): string {
  const abs = Math.abs(ms);
  const seconds = Math.floor(abs / 1000);
  if (seconds < 1) return 'same time';
  if (seconds < 60) return `${seconds}s earlier`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (seconds < 3600) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s earlier` : `${minutes}m earlier`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m earlier` : `${hours}h earlier`;
}

interface HistoryTimelineProps {
  entityType: EntityType;
  entityId: number;
}

export const EntityHistoryTimeline: React.FC<HistoryTimelineProps> = ({ entityType, entityId }) => {
  const [history, setHistory] = useState<EntityHistoryType | null>(null);
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedWorksheets, setExpandedWorksheets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [historyData, eventsData] = await Promise.all([
          limsClient.getEntityHistory(entityType, entityId),
          limsClient.getEvents({ entityType, entityId }),
        ]);

        const processedHistory: EntityHistoryType = {
          ...historyData,
          snapshots: historyData.snapshots.map(snapshot => ({
            ...snapshot,
            timestamp: new Date(snapshot.timestamp),
          })),
          attributeChanges: historyData.attributeChanges.map(change => ({
            ...change,
            validFrom: new Date(change.validFrom),
            validTo: change.validTo ? new Date(change.validTo) : null,
          })),
        };

        setHistory(processedHistory);
        setEvents(eventsData.data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load history';
        setError(errorMessage);
        console.error('Failed to load entity history:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [entityType, entityId]);

  const formatValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === 'null') {
      return <span className="tl-val-null">null</span>;
    }
    if (value === true || value === 'true') {
      return <span className="tl-val-bool tl-val-true">true</span>;
    }
    if (value === false || value === 'false') {
      return <span className="tl-val-bool tl-val-false">false</span>;
    }
    return <>{formatValueStr(value)}</>;
  };

  const WORKSHEET_ATTRS: Record<string, string> = {
    raw_worksheet_data: 'Raw Worksheet Data',
    worksheet_data: 'Worksheet Data',
  };

  const toggleWorksheet = (key: string) => {
    setExpandedWorksheets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const timeline = useMemo((): TimelineEntry[] => {
    if (!history) return [];

    const entries: TimelineEntry[] = [];

    const sortedSnapshots = [...history.snapshots].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    sortedSnapshots.forEach((snapshot, index) => {
      const isCreation = index === 0;
      const changes: Array<{ attr: string; oldValue: any; newValue: any }> = [];

      if (isCreation) {
        Object.entries(snapshot.attributes).forEach(([attr, newValue]) => {
          changes.push({ attr, oldValue: undefined, newValue });
        });
      } else {
        const prevSnapshot = sortedSnapshots[index - 1];
        Object.keys(snapshot.attributes).forEach(attr => {
          const newVal = snapshot.attributes[attr];
          const oldVal = prevSnapshot.attributes[attr];
          const changed = typeof oldVal === 'object' || typeof newVal === 'object'
            ? JSON.stringify(oldVal) !== JSON.stringify(newVal)
            : oldVal !== newVal;
          if (changed) {
            changes.push({ attr, oldValue: oldVal, newValue: newVal });
          }
        });
        Object.keys(prevSnapshot.attributes).forEach(attr => {
          if (!(attr in snapshot.attributes)) {
            changes.push({ attr, oldValue: prevSnapshot.attributes[attr], newValue: undefined });
          }
        });
      }

      entries.push({
        type: 'snapshot',
        timestamp: new Date(snapshot.timestamp),
        version: snapshot.version,
        isCreation,
        changes: changes.sort((a, b) => a.attr.localeCompare(b.attr)),
      });
    });

    events.forEach(event => {
      entries.push({
        type: 'event',
        timestamp: new Date(event.createdAt),
        event,
      });
    });

    entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return entries;
  }, [history, events]);

  if (loading) {
    return (
      <div className="entity-history-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="entity-history-error">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!history || history.snapshots.length === 0) {
    return (
      <div className="entity-history-empty">
        <p>No history available for this entity.</p>
      </div>
    );
  }

  const snapshotCount = history.snapshots.length;
  const eventCount = events.length;

  return (
    <>
      <div className="entity-history-header">
        <span className="history-stats-inline">
          {snapshotCount} snapshot{snapshotCount !== 1 ? 's' : ''} · {eventCount} event{eventCount !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="timeline">
        <div className="tl-now">
          <div className="tl-now-dot" />
          <span className="tl-now-label">Current state</span>
        </div>
        {timeline.map((entry, index) => {
            const prevEntry = index > 0 ? timeline[index - 1] : null;
            const deltaMs = prevEntry
              ? Math.abs(entry.timestamp.getTime() - prevEntry.timestamp.getTime())
              : null;

            return (
              <React.Fragment key={`${entry.type}-${entry.timestamp.getTime()}-${entry.type === 'snapshot' ? entry.version : entry.event.id}`}>
                {deltaMs !== null && (
                  <div className="time-delta">
                    <span className="time-delta-text">{formatTimeDelta(deltaMs)}</span>
                  </div>
                )}

                {entry.type === 'snapshot' ? (
                  <div className={`tl-entry tl-snapshot ${entry.isCreation ? 'tl-creation' : ''}`}>
                    <div className="tl-dot tl-dot-blue" />
                    <div className="tl-body">
                      <div className="tl-head">
                        <span className="tl-label">
                          {entry.isCreation ? <><CreateIcon /> Created</> : <><EditIcon /> Attribute changed</>}
                        </span>
                        <span className="tl-meta">
                          <span className="tl-version">v{entry.version}</span>
                          <span className="tl-time">{formatDate(entry.timestamp)}</span>
                        </span>
                      </div>
                      <div className="tl-changes">
                        {entry.changes.map((c) => {
                          const wsLabel = WORKSHEET_ATTRS[c.attr];
                          if (wsLabel && c.newValue && typeof c.newValue === 'object') {
                            const wsKey = `${index}-${c.attr}`;
                            const isExpanded = expandedWorksheets[wsKey] ?? false;
                            return (
                              <div key={c.attr} className="tl-worksheet-accordion">
                                <button
                                  className="tl-worksheet-toggle"
                                  onClick={() => toggleWorksheet(wsKey)}
                                >
                                  <span className={`tl-worksheet-chevron${isExpanded ? ' expanded' : ''}`}>&#9654;</span>
                                  <span className="tl-attr">{c.attr}</span>
                                  <span className="tl-worksheet-label">{entry.isCreation ? 'set' : 'changed'}</span>
                                </button>
                                {isExpanded && (
                                  <div className="tl-worksheet-content">
                                    {c.attr === 'raw_worksheet_data' ? (
                                      <WorksheetDiffViewer
                                        oldData={entry.isCreation ? null : c.oldValue}
                                        newData={c.newValue}
                                      />
                                    ) : (
                                      <KeyValueDiffViewer
                                        oldData={entry.isCreation ? null : c.oldValue}
                                        newData={c.newValue}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div key={c.attr} className="tl-change-row">
                              {entry.isCreation ? (
                                <>
                                  <span className="tl-attr">{c.attr}</span>
                                  <span className="tl-eq">=</span>
                                  <span className="tl-val">{formatValue(c.newValue)}</span>
                                </>
                              ) : (
                                <>
                                  <span className="tl-attr">{c.attr}</span>
                                  <span className="tl-eq">:</span>
                                  <span className="tl-val-old">{formatValue(c.oldValue)}</span>
                                  <span className="tl-arrow">→</span>
                                  <span className="tl-val">{formatValue(c.newValue)}</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                        {!entry.isCreation && entry.changes.length === 0 && (
                          <div className="tl-change-row">
                            <span className="tl-no-changes">No attribute changes detected</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`tl-entry tl-event ${getEventStatusClass(entry.event)}`}>
                    <div className={`tl-dot ${getEventDotClass(entry.event)}`} />
                    <div className="tl-body">
                      <div className="tl-head">
                        <span className="tl-label">
                          <EventIcon />
                          Event #{entry.event.id}
                          <span className="tl-action-badge">{entry.event.action}</span>
                        </span>
                        <span className="tl-meta">
                          <span className="tl-time">{formatDate(entry.timestamp)}</span>
                        </span>
                      </div>
                      {entry.event.listeners.length > 0 && (
                        <div className="tl-listeners">
                          {entry.event.listeners.map((listener, li) => (
                            <div key={li} className={`tl-listener tl-listener-${listener.status}`}>
                              <span className="tl-listener-name">{listener.name}</span>
                              <span className="tl-listener-sep">:</span>
                              <span className="tl-listener-status">
                                {listener.status === 'processed' && '✓'}
                                {listener.status === 'skipped' && '–'}
                                {listener.status === 'error' && '✗'}
                                {' '}{listener.status}
                              </span>
                              {listener.durationMs > 0 && (
                                <span className="tl-listener-dur">({listener.durationMs}ms)</span>
                              )}
                              {listener.result?.message && (
                                <span className="tl-listener-err">{listener.result.message}</span>
                              )}
                              {listener.result && !listener.result.message && (
                                <ExpandableJson value={JSON.stringify(listener.result, null, 2)} />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </>
    );
};

function getEventDotClass(event: EventLogEntry): string {
  const hasError = event.listeners.some(l => l.status === 'error');
  if (hasError) return 'tl-dot-red';
  const allSkipped = event.listeners.length > 0 && event.listeners.every(l => l.status === 'skipped');
  if (allSkipped) return 'tl-dot-yellow';
  return 'tl-dot-green';
}

function getEventStatusClass(event: EventLogEntry): string {
  const hasError = event.listeners.some(l => l.status === 'error');
  if (hasError) return 'tl-event-error';
  const allSkipped = event.listeners.length > 0 && event.listeners.every(l => l.status === 'skipped');
  if (allSkipped) return 'tl-event-skipped';
  return '';
}
